import React from 'react';
import { Popper, PopperProps } from '@mui/material';
import { ChartAccessorFunctions, ChartFormatters, WithColorAccessorFunctions } from '../Chart.types';
import { useChartAccessorsContext } from '../ChartRoot';
import { useChartSurfaceContext } from '../ChartSurface';
import { useChartTooltipContext } from './ChartTooltipProvider';

export type TooltipProps<Datum> = {
  tooltipData: Datum;
  isPinned: boolean;
} & ChartAccessorFunctions<Datum> &
  ChartFormatters<Datum> &
  WithColorAccessorFunctions<Datum>;

export type TooltipComponent<Datum = unknown, P = {}> = React.ComponentType<TooltipProps<Datum> & P>;

type Props<Datum, P = {}> = Omit<PopperProps, 'children' | 'open' | 'anchorEl' | 'modifiers'> & {
  TooltipComponent?: React.ComponentType<TooltipProps<Datum> & P>;
  tooltipComponentProps?: Omit<NoInfer<P>, keyof TooltipProps<Datum>>;
};

export const Tooltip = <Datum, P = {}>({ TooltipComponent = () => null, tooltipComponentProps, ...props }: Props<Datum, P>) => {
  const { anchorEl, popperRef, tooltipData, tooltipOpen, tooltipRef, isPinned } = useChartTooltipContext<Datum>();
  const accessors = useChartAccessorsContext<Datum>();
  const { surfaceRef } = useChartSurfaceContext();

  const modifiersWithBoundary = React.useMemo(
    () => [
      { name: 'offset' as const, options: { offset: () => [16, 16] } },
      { name: 'preventOverflow' as const, options: { altAxis: true, boundary: surfaceRef.current } },
      { name: 'flip' as const, options: { fallbackPlacements: ['top', 'right'], boundary: surfaceRef.current } },
    ],
    [surfaceRef]
  );

  return (
    <Popper
      anchorEl={anchorEl}
      open={Boolean(tooltipOpen && tooltipData)}
      modifiers={modifiersWithBoundary}
      popperRef={popperRef}
      ref={tooltipRef}
      sx={{ pointerEvents: isPinned ? 'auto' : 'none', zIndex: t => t.zIndex.tooltip }}
      placement="bottom-start"
      {...props}>
      {TooltipComponent && tooltipData ? (
        <TooltipComponent {...(tooltipComponentProps as P)} tooltipData={tooltipData} isPinned={isPinned} {...accessors} />
      ) : null}
    </Popper>
  );
};
